import { sanitizeUrl } from '@globals/personalIdentifierInfo/pIISanitizeUtils'
import type { Dictionary } from 'rollbar'

// TODO - move these into environment variables potentially to avoid hard coding our domains
// OK to leave as is for testing purposes but should be refactored once we extend the list of domains
export const ROLLBAR_HOST_SAFE_LIST: string[] = [
  'kudos.com',
  'kudosapp.dev',
  'kudosnow-development-sandbox-v2.com',
  'kudosnow-staging-k2.com',
]

/** `transform` receives a `Dictionary` (index signature of `unknown`), so narrow before walking. */
const isDictionary = (value: unknown): value is Dictionary =>
  typeof value === 'object' && value !== null

export type RollbarPlatform = 'android' | 'ios' | 'web'

/**
 * Classifies the reporting platform from the user-agent Rollbar already attached
 * at `client.javascript.browser`, keeping this a pure function of the payload
 * rather than a `navigator` read.
 *
 * iPadOS 13+ sends a Macintosh user-agent indistinguishable from desktop Safari,
 * so it classifies as `web`. The native app WebViews report `iPhone`/`iPad`.
 */
export const classifyRollbarPlatform = (userAgent: string | undefined): RollbarPlatform => {
  if (!userAgent) return 'web'

  const lowerCaseUserAgent = userAgent.toLowerCase()
  if (lowerCaseUserAgent.includes('android')) return 'android'
  if (/iphone|ipad|ipod/.test(lowerCaseUserAgent)) return 'ios'
  return 'web'
}

const readUserAgent = (data: Dictionary): string | undefined => {
  const client = data.client
  if (!isDictionary(client)) return undefined

  const javascript = client.javascript
  if (!isDictionary(javascript)) return undefined

  return typeof javascript.browser === 'string' ? javascript.browser : undefined
}

const readTrace = (body: Dictionary): Dictionary | undefined => {
  if (isDictionary(body.trace)) return body.trace

  const chain = body.trace_chain
  if (Array.isArray(chain) && isDictionary(chain[0])) return chain[0]

  return undefined
}

type RollbarException = {
  className: string
  message: string | undefined
  description: string | undefined
}

const readException = (data: Dictionary): RollbarException | null => {
  const body = data.body
  if (!isDictionary(body)) return null

  const trace = readTrace(body)
  if (!trace || !isDictionary(trace.exception)) return null

  const { class: className, message, description } = trace.exception

  return {
    className: typeof className === 'string' ? className : 'Error',
    message: typeof message === 'string' ? message : undefined,
    description: typeof description === 'string' ? description : undefined,
  }
}

/**
 * A description that differs from the exception message is the label we passed
 * at the call site (`rollbar.warning('Login MFA verification unsuccessful', …)`),
 * which makes it a safe grouping key. When they match, Rollbar merely copied the
 * message, so the default grouping is left alone.
 */
const hasDistinctDescription = (exception: RollbarException): boolean =>
  Boolean(exception.description) && exception.description !== exception.message

/**
 * Redacts nested PII from the page URL Rollbar attaches to every event.
 *
 * `scrubFields` covers top-level `email=`-style params in request bodies and
 * similar fields. It cannot reach PII nested inside an encoded redirect
 * (`?next=%2Fusers%3Femail%3D…`), so `sanitizeUrl` is applied to the URL fields
 * specifically.
 */
export const sanitizeRollbarRequestUrls = (data: Dictionary): void => {
  const request = data.request
  if (!isDictionary(request)) return

  if (typeof request.url === 'string') request.url = sanitizeUrl(request.url)
  if (typeof request.query_string === 'string') {
    request.query_string = sanitizeUrl(request.query_string)
  }
}

/**
 * Stamps the platform so occurrences can be filtered with `custom[platform]:ios`,
 * and — for errors we labeled ourselves — groups and titles them by that label so
 * they no longer fall into the generic `Request failed with status code 400` item.
 *
 * The exception's own `description` and `message` are left untouched.
 */
export const labelRollbarPlatform = (data: Dictionary): void => {
  const existingCustom = isDictionary(data.custom) ? data.custom : {}
  data.custom = {
    ...existingCustom,
    platform: classifyRollbarPlatform(readUserAgent(data)),
  }

  const exception = readException(data)
  if (!exception || !hasDistinctDescription(exception)) return

  const label = `${exception.className}: ${exception.description}`
  data.title = label
  data.fingerprint = label
}

/** Single `transform` entry point: redact PII from URLs, then label the payload. */
export const transformRollbarPayload = (data: Dictionary): void => {
  sanitizeRollbarRequestUrls(data)
  labelRollbarPlatform(data)
}
